import React, { useCallback, useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import styled from "@mui/material/styles/styled";
import Star from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import CloseIconButton from "fe-common/ui/icons/closeIconButton";
import DialogContent from "@mui/material/DialogContent";
import Rating from "@mui/lab/Rating";
import FavoriteIcon from "@mui/icons-material/Favorite";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import { CircularProgress } from "@mui/material";
import { useCreateSharedCase } from "../hooks/useCreateSharedCase";
import { useDiagnosticAgentType } from "../../../context/diagnosticAgent";
import { useShareLinks } from "../../../saveCase/useShareLinks";
import { useDaoContext } from "fe-web-components/diseaseview/DaoContext";
import { getCurrentUserEmail } from "user-management/src/cognito/kahunUserAuth";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import { usePatientCase } from "../usePatientCase";
import { ReactComponent as HandsIcon } from "../../../assets/clinic/hands.svg";

//region [[ Styles ]]

const ContentHeader = styled("div")({
  margin: "0 10px",
  textAlign: "center",
  fontSize: "20px",
  fontWeight: "bold",
  marginBottom: "20px",
});

const ContentText = styled("div")({
  margin: "0 10px",
  textAlign: "center",
  fontSize: "16px",
  marginBottom: "20px",
});

const ContentTextLeft = styled("div")({
  textAlign: "left",
  marginTop: 10,
});

const RatingBox = styled(Rating)({
  margin: "8px",
});

const HeartIcon = styled(FavoriteIcon)({
  position: "relative",
  top: "6px",
  right: "4px",
  color: KahunColorMap.PrimaryDim,
});

const ActionButton = styled(Button)({
  marginTop: "10px",
  fontSize: "16px",
  textTransform: "capitalize",
  boxShadow: "none",
  backgroundColor: KahunColorMap.PrimaryDim,
});

const FeedbackInput = styled(TextField)({
  marginTop: "10px",
  border: `1px solid ${KahunColorMap.InputBorder}`,
  borderRadius: "5px",
});

const EmailInput = styled(TextField)({
  marginTop: "10px",
  border: `1px solid ${KahunColorMap.InputBorder}`,
  borderRadius: "5px",
});

const CheckboxControl = styled(FormControlLabel)({
  display: "flex",
});

const CheckboxControlLabel = styled("span")({
  fontSize: "14px",
});
//endregion [[ Styles ]]

//region [[ Props ]]
interface SendFeedbackDialogProps {
  isOpen: boolean;
  withCaseId?: string;
  clinicName?: string;
  onClose: () => void;
}

enum sendStatus {
  none,
  success,
  fail,
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const SendFeedbackDialog = ({
  isOpen,
  onClose,
  withCaseId,
  clinicName,
}: SendFeedbackDialogProps) => {
  const [agentType] = useDiagnosticAgentType();
  const createSharedCase = useCreateSharedCase(agentType);
  const { patientCase } = usePatientCase();
  const { shareLink } = useShareLinks();
  const dao = useDaoContext();
  const [rating, setRating] = useState<number>(0);
  const [feedbackText, setFeedbackText] = useState<string>("");
  const [isShareCaseChecked, setIsShareCaseChecked] = useState<boolean>(true);
  const [showShareCase, setShowCaseShare] = useState<boolean>(false);
  const [email, setEmail] = useState<string>("");
  const [sending, setSending] = useState<boolean>(false);
  const [sent, setSent] = useState<sendStatus>(sendStatus.none);

  useEffect(
    () => setShowCaseShare(!!withCaseId || patientCase?.main?.length > 0),
    [withCaseId, patientCase],
  );

  useEffect(() => {
    getCurrentUserEmail().then((email) => setEmail(email));
  }, []);

  const sendFeedback = useCallback(async () => {
    setSending(true);

    let success,
      urlPrefix = "",
      caseLink = "",
      caseId = "";

    try {
      if (showShareCase && isShareCaseChecked) {
        if (withCaseId) {
          //duplicate the selected case and attach the new id to the url prefix
          caseId = withCaseId;
          urlPrefix = shareLink("");
        } else {
          //save current case (if exists) and create a link for sharing *this is only used in the Agent
          const patientCaseId = await createSharedCase();
          caseLink = shareLink(patientCaseId);
        }
      }
      await dao.sendFeedback(
        feedbackText,
        caseLink,
        email,
        caseId,
        urlPrefix,
        clinicName,
        rating,
      );
      success = "OK";
      setSent(sendStatus.success);
    } catch (e) {
      success = `Failed: ${e["message"]}`;
      setSent(sendStatus.fail);
    } finally {
      setSending(false);
      evt({
        category: CATEGORY.AGENT,
        action: ACTION.SEND_FEEDBACK,
        props: {
          clinicName,
          userEmail: email,
          feedbackText,
          caseLink,
          rating,
          sent: success,
        },
      });
    }
  }, [
    rating,
    feedbackText,
    isShareCaseChecked,
    showShareCase,
    email,
    createSharedCase,
    dao,
    withCaseId,
    clinicName,
    shareLink,
  ]);

  const onShareCaseCheckChange = (event) => {
    setIsShareCaseChecked(event.target.checked);
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      PaperProps={{ style: { margin: "8px" } }}
    >
      <DialogTitle>
        <CloseIconButton onClick={onClose} />
      </DialogTitle>
      <DialogContent>
        {sent === sendStatus.none && (
          <>
            <ContentHeader>How would you rate Kahun?</ContentHeader>
            <ContentText>
              <RatingBox
                value={rating}
                icon={
                  <StarBorderIcon style={{ color: KahunColorMap.PrimaryDim }} />
                }
                emptyIcon={<Star />}
                onChange={(event, newValue) => {
                  setRating(newValue);
                }}
                disabled={sending}
              />
              <div>
                <HeartIcon />
                Let us know what you think:
              </div>
              <FeedbackInput
                multiline
                fullWidth
                rows={3}
                variant="outlined"
                onChange={(e) => setFeedbackText(e.target.value)}
                disabled={sending}
              />
              <div>
                {showShareCase && (
                  <CheckboxControl
                    control={
                      <Checkbox
                        checked={isShareCaseChecked}
                        onChange={onShareCaseCheckChange}
                        name="checkedB"
                        color="primary"
                        disabled={sending}
                      />
                    }
                    label={
                      <CheckboxControlLabel>
                        {`The feedback relates to ${
                          withCaseId ? "selected" : "current"
                        } case`}
                      </CheckboxControlLabel>
                    }
                  />
                )}
              </div>
              {!email && (
                <>
                  <ContentTextLeft>
                    If you would like us to get back you you,
                    <br />
                    please add your email address here
                  </ContentTextLeft>

                  <EmailInput
                    placeholder="email address"
                    type="email"
                    variant="outlined"
                    size="small"
                    fullWidth
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={sending}
                  />
                </>
              )}

              <ActionButton
                fullWidth
                color="primary"
                variant="contained"
                onClick={sendFeedback}
                disabled={sending}
              >
                {sending ? (
                  <>
                    <CircularProgress size={16} style={{ marginRight: 8 }} />
                    Sending your feedback
                  </>
                ) : (
                  "Send"
                )}
              </ActionButton>
            </ContentText>
          </>
        )}
        {sent === sendStatus.success && (
          <ContentText>
            <HandsIcon />
            <div style={{ marginTop: 14 }}>Thank you for your feedback!</div>
          </ContentText>
        )}
        {sent === sendStatus.fail && (
          <ContentText>
            Sorry!
            <br />
            we could not send your feedback
            <br />
            <ActionButton
              color="primary"
              variant="contained"
              onClick={() => setSent(sendStatus.none)}
            >
              Please try again
            </ActionButton>
          </ContentText>
        )}
      </DialogContent>
    </Dialog>
  );
};
